import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button } from 'components/components/atoms';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import cx from 'classnames';
import { useSpring, animated } from '@react-spring/web';
import useMeasure from 'utils/hooks/useMeasure';
import { hasPrice } from 'utils/parsePrice';

const CardContainer = styled.div`
  width: 100%;
  margin-top: 0.313rem;
  margin-bottom: 0.25rem;
`;

const Card = styled.div`
  margin-bottom: 0.25rem;
`;

const AvailableFlag = styled.div`
  border-radius: 0.813rem;
  min-height: 1.625rem;
  width: fit-content;
  min-width: 7.5rem;
`;

const CardTitle = styled.div`
  letter-spacing: -0.029rem;
`;

const CardDescription = styled.div.attrs({
  className:
    'font-graphie text-mattamy-blue text-base leading-relaxed md:leading-5  md:mb-0 flex items-start',
})`
  letter-spacing: 0.007rem;
`;

const ToggleScheduleContainer = styled.div`
  place-self: center;
`;
const Img = styled.img`
  min-height: 4.75rem;
  width: 10rem;
`;

const IconContainer = styled.div.attrs({
  className:
    'flex justify-center items-center w-30-mattamy h-30-mattamy bg-action-blue inline mr-2 rounded-full text-white',
})`
  min-width: 1.875rem;
`;

const StyledButton = styled(Button)`
  height: 3.125rem;
`;

const CommunityCardMobile = (props) => {
  const {
    imageUrl,
    imageAlt,
    bubbletext,
    paragraph,
    title,
    subtitle,
    startingFromLabel,
    startingFrom,
    noPriceText,
    page,
    counter,
    numberPerPage,
    ctas,
  } = props;

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isTablet = ['md'].includes(breakpoint);

  const cta = ctas && ctas[0];
  const [cardOpen, setCardOpen] = useState(false);
  const [cardRefHeight, { height: measuredHeight }] = useMeasure();
  const expandCard = useSpring({
    height: cardOpen ? measuredHeight : 0,
    opacity: cardOpen ? 1 : 0,
    pointerEvents: cardOpen ? 'all' : 'none',
  });

  const maxCounter = page * numberPerPage;
  const hasStartingFromPrice = hasPrice(startingFrom);
  const priceText = hasStartingFromPrice
    ? startingFromLabel + ' ' + startingFrom
    : noPriceText;

  return (
    <CardContainer className={maxCounter > counter ? '' : 'hidden'}>
      <Card className='w-full bg-mattamy-white flex flex-col lg:flex-row justify-center'>
        <div className='flex flex-row w-full'>
          <div className='text-center relative rounded overflow-hidden w-3/12 h-16'>
            <a
              href={cta && cta.href}
              target={(cta && cta.target) || '_self'}
              aria-label={
                'View ' +
                title +
                ' information. ' +
                (cta && cta.target && cta.target === '_blank'
                  ? '(Opens in new tab)'
                  : '')
              }
            >
              <Img
                src={imageUrl}
                alt={imageAlt || ''}
                className={'bg-gray-300 object-cover rounded'}
              />
            </a>
          </div>

          <div className='min-h-16 ml-2 text-left w-7/12 flex flex-col'>
            <a
              href={cta && cta.href}
              target={(cta && cta.target) || '_self'}
              aria-label={
                'View ' +
                title +
                ' information. ' +
                (cta && cta.target && cta.target === '_blank'
                  ? '(Opens in new tab)'
                  : '')
              }
            >
              {bubbletext && (
                <div className='flex flex-row w-full'>
                  <AvailableFlag
                    islogo={false}
                    className='flex items-center justify-center bg-white border-mattamy-blue-light border-2 border-mattamy-blue-light hover:md:bg-mattamy-blue-light
                  mt-1 md:mt-0 hover:md:text-white text-mattamy-blue-light px-2'
                  >
                    <p className='px-1 text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1'>
                      {bubbletext}
                    </p>
                  </AvailableFlag>
                </div>
              )}

              <CardTitle className=' w-full font-trade-gothic-20 text-mattamy-blue text-xl leading-8 block'>
                {title}
              </CardTitle>
            </a>
          </div>

          <div className=' h-auto text-left w-2/12 flex flex-col items-center'>
            <button
              className='relative w-10 h-10 lg:w-20 lg:h-20'
              onClick={() => setCardOpen((v) => !v)}
              aria-label={
                cardOpen
                  ? 'Collapse Information'
                  : 'Expand for more information'
              }
            >
              <div
                className={cx(
                  'absolute top-0 right-0 left-0 bottom-0 m-auto w-8/12 h-1 bg-action-blue rounded transform transition-transform duration-700',
                  { 'rotate-90': !cardOpen },
                )}
              />
              <div className='absolute top-0 right-0 left-0 bottom-0 m-auto w-8/12 h-1 rounded bg-action-blue' />
            </button>
          </div>
        </div>

        <animated.div className='w-full overflow-hidden' style={expandCard}>
          <div ref={cardRefHeight} className='flex flex-col mt-2 w-full'>
            <a
              href={cta && cta.href}
              target={(cta && cta.target) || '_self'}
              aria-label={
                title +
                ' ' +
                subtitle +
                ' ' +
                paragraph +
                priceText +
                '. View ' +
                title +
                ' information. ' +
                (cta && cta.target && cta.target === '_blank'
                  ? '(Opens in new tab)'
                  : '')
              }
            >
              <div className='flex flex-col w-full'>
                <div className='pt-2 pb-4 lg:pt-0 w-full md:w-1/2 flex flex-col justify-between'>
                  <div className='block lg:pl-5'>
                    {subtitle && (
                      <CardDescription className='items-start text-left'>
                        <p className='break-normal mt-1'>{subtitle}</p>
                      </CardDescription>
                    )}
                    {paragraph && (
                      <CardDescription className='items-start pb-2 text-left'>
                        <p className='break-normal mt-1'>{paragraph}</p>
                      </CardDescription>
                    )}
                    {((!hasStartingFromPrice && noPriceText) ||
                      (hasStartingFromPrice && startingFromLabel)) && (
                      <StyledButton
                        color={'action-blue'}
                        variant='filled'
                        className='block text-base mt-2 pt-0 pb-0 md:w/full'
                        buttonDescription={priceText}
                      >
                        {priceText}
                      </StyledButton>
                    )}
                  </div>
                </div>
              </div>
            </a>
          </div>
        </animated.div>
      </Card>
    </CardContainer>
  );
};

CommunityCardMobile.propTypes = {
  props: PropTypes.object,
};

export default CommunityCardMobile;
